import { useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    useCreate,
    useGetIdentity,
    useGetList,
    useNotify,
    useRedirect,
} from 'react-admin';
import { UserPlus } from 'lucide-react';
import { DialogCloseButton } from '../misc/DialogCloseButton';
import { Account } from '../types';
import {
    useContactDuplicateCheck,
    DuplicateWarning,
} from '../misc/DuplicateWarning';

interface ContactCreateDialogProps {
    open: boolean;
    onClose: () => void;
}

export const ContactCreateDialog = ({
    open,
    onClose,
}: ContactCreateDialogProps) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [title, setTitle] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [selectedAccount, setSelectedAccount] = useState<Account | null>(
        null
    );

    const [create, { isPending }] = useCreate();
    const { identity } = useGetIdentity();
    const notify = useNotify();
    const redirect = useRedirect();

    const duplicateMatches = useContactDuplicateCheck(
        firstName,
        lastName,
        email
    );

    const { data: accounts, isPending: accountsLoading } = useGetList<Account>(
        'accounts',
        {
            pagination: { page: 1, perPage: 200 },
            sort: { field: 'name', order: 'ASC' },
        }
    );

    const handleSubmit = async () => {
        if (!firstName.trim() || !lastName.trim()) {
            notify('First name and last name are required', {
                type: 'warning',
            });
            return;
        }

        try {
            const contactData = {
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                title: title.trim() || undefined,
                account_id: selectedAccount?.id || undefined,
                email_jsonb: email.trim()
                    ? [{ email: email.trim(), type: 'Work' }]
                    : [],
                phone_jsonb: phone.trim()
                    ? [
                          {
                              number: phone.trim(),
                              type: 'Work',
                          },
                      ]
                    : [],
                sales_id: identity?.id,
                first_seen: new Date().toISOString(),
                last_seen: new Date().toISOString(),
                tags: [],
            };

            const result = await create(
                'contacts',
                { data: contactData },
                { returnPromise: true }
            );

            notify('Contact created successfully', {
                type: 'success',
            });
            resetAndClose();
            if (result?.id) {
                redirect('show', 'contacts', result.id);
            }
        } catch {
            notify('Error creating contact', {
                type: 'error',
            });
        }
    };

    const resetAndClose = () => {
        setFirstName('');
        setLastName('');
        setTitle('');
        setPhone('');
        setEmail('');
        setSelectedAccount(null);
        onClose();
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            fontSize: '0.875rem',
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e2e8f0',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#cbd5e1',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#10b981',
            },
        },
        '& .MuiInputLabel-root': {
            fontSize: '0.875rem',
            color: '#64748b',
            '&.Mui-focused': {
                color: '#10b981',
            },
        },
    };

    return (
        <Dialog
            open={open}
            onClose={resetAndClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    overflow: 'hidden',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(15, 23, 42, 0.6)',
                    },
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    background:
                        'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
                    borderBottom: '1px solid #d1fae5',
                    px: 3,
                    py: 2.5,
                    position: 'relative',
                }}
            >
                <DialogCloseButton
                    onClose={resetAndClose}
                    top={12}
                    right={12}
                />
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            backgroundColor: '#d1fae5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <UserPlus size={20} color="#059669" />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                color: '#0f172a',
                            }}
                        >
                            Create New Contact
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.8125rem',
                                color: '#64748b',
                            }}
                        >
                            Add a new contact to your CRM
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <DialogContent sx={{ px: 3, py: 2.5 }}>
                {/* Contact Information Section */}
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mb: 2,
                    }}
                >
                    Contact Information
                </Typography>

                <Stack spacing={2}>
                    {/* Name Row */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="First Name"
                            required
                            fullWidth
                            size="small"
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                            sx={fieldSx}
                        />
                        <TextField
                            label="Last Name"
                            required
                            fullWidth
                            size="small"
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                            sx={fieldSx}
                        />
                    </Stack>

                    <DuplicateWarning
                        matches={duplicateMatches}
                        entityType="contact"
                    />

                    {/* Role/Title */}
                    <TextField
                        label="Role / Title"
                        fullWidth
                        size="small"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        sx={fieldSx}
                    />

                    {/* Phone & Email Row */}
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Phone Number"
                            fullWidth
                            size="small"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            sx={fieldSx}
                        />
                        <TextField
                            label="Email Address"
                            fullWidth
                            size="small"
                            type="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            sx={fieldSx}
                        />
                    </Stack>
                </Stack>

                {/* Related Account Section */}
                <Typography
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        mt: 3,
                        mb: 2,
                    }}
                >
                    Related Account
                </Typography>

                <Autocomplete
                    options={accounts || []}
                    getOptionLabel={(option: Account) => option.name || ''}
                    value={selectedAccount}
                    onChange={(_e, newValue) => setSelectedAccount(newValue)}
                    loading={accountsLoading}
                    size="small"
                    renderInput={params => (
                        <TextField
                            {...params}
                            label="Search accounts..."
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {accountsLoading ? (
                                            <CircularProgress
                                                color="inherit"
                                                size={18}
                                            />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={fieldSx}
                        />
                    )}
                    noOptionsText="No accounts found"
                    sx={{
                        '& .MuiAutocomplete-listbox': {
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: '1px solid #e2e8f0',
                    gap: 1.5,
                }}
            >
                <Button
                    onClick={resetAndClose}
                    sx={{
                        textTransform: 'none',
                        color: '#64748b',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        borderRadius: '8px',
                        px: 2.5,
                        '&:hover': {
                            backgroundColor: '#f1f5f9',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isPending}
                    startIcon={
                        isPending ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <UserPlus size={16} />
                        )
                    }
                    sx={{
                        textTransform: 'none',
                        backgroundColor: '#10b981',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        borderRadius: '8px',
                        px: 2.5,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: '#059669',
                            boxShadow: 'none',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#86efac',
                        },
                    }}
                >
                    Create Contact
                </Button>
            </DialogActions>
        </Dialog>
    );
};
